import { createImmerReducer } from 'common/reducerBase';
import { searchResults } from 'tabs/GoogleDrive/data/predefinedTreeNodes';
import { doListsSelection, doListsMultipleSelection } from '../../common/reducerMethods';
import GDriveRecord, { getNewContextList, getDefaultSortOrder } from '../data/GDriveRecord';
import { types } from '../actions';
import consts, { STATUSES, ORDER_BY, SORT, SEARCH_RESULTS_ID } from '../data/consts';

const { ASSETS } = consts.CONTEXTS;
const { SEARCH } = consts.CONTEXTS;

const {
  LOAD_TREE_FOLDERS,
  LOAD_NAVIGATION_FOLDERS,
  LOAD_NAVIGATION_FILES,
  LOAD_SEARCH_FOLDERS,
  LOAD_SEARCH_FILES,
  SET_ORDER_BY,
  SET_SORT,
  CLEAR_SEARCH_TERM,
  SET_NAVIGATION_CURRENT_FOLDER,
  SET_AUTH_INFO,
  SET_AUTH_START,
  SET_FILE_SELECTION,
  SET_MULTI_FILE_SELECTION,
  SET_USER_LOGOUT_SUCCESS,
  SET_SEARCH_TERM,
  TOGGLE_PANEL,
  CLEAR_IMAGE_SELECTION,
  SET_BREADCRUMBS_PATH,
  SET_FETCHING_FILE_DATA,
  CLEAR_TAB_STATE,
  SET_WAITING_SIGN_IN,
  SET_USER_START_SIGN_IN,
} = types;

const setSearch = (draft, options = {}) => {
  const { term, keepCurrentData } = options;

  incrementRequestId(draft);

  if (keepCurrentData) {
    draft.lists[SEARCH].params.term = term;
  } else {
    const { ids } = draft.lists[SEARCH].folders;
    draft.lists[SEARCH] = getNewContextList(SEARCH, ids, { term });
  }

  // determine in which context we are in based on whether search term was set or not
  draft.currentContext = term ? SEARCH : ASSETS;
};

const addTreeFolders = (tree, result, status) => {
  const { id, items, cursor } = result;

  if (status) {
    tree.statuses[id] = status;
  }

  if (!tree.folders[id]) {
    tree.folders[id] = { children: [] };
  } else if (!tree.folders[id].children) {
    tree.folders[id].children = [];
  }

  tree.folders[id].cursor = cursor;

  const newIds = [];

  items.forEach((item) => {
    tree.folders[item.id] = { ...tree.folders[item.id], ...item };
    newIds.push(item.id);
  });

  const children = tree.folders[id].children.concat(newIds);

  tree.folders[id].children = [...new Set(children)];
};

const updateTreeFoldersFromNavigation = (draft, result, options) => {
  const { orderBy, sort, tree, lists } = draft;
  const { id } = lists[ASSETS].params;

  // only do a complete sync when we're in the default order/sort OR when forced manually
  if ((orderBy === ORDER_BY.NAME && sort === SORT.ASC) || options.forceTreeUpdate) {
    addTreeFolders(
      tree,
      {
        ...result,
        id,
      },
      STATUSES.SUCCESS
    );
  }
  // add the folder user navigation so its added to the tree as a child of its parent
  // does not apply for root elements with no parents (recent, starred, etc.)
  else if (options.previousFolder && options.previousFolder.parents) {
    // find the id for the parent folder
    const previous = options.previousFolder;
    const parentId = previous.parents[0];

    addTreeFolders(draft.tree, { id: parentId, items: [previous] }, null);
  }
};

const updateFoldersIds = (ids, folders) => {
  folders.forEach((folder) => {
    const { id, name, path } = folder;
    ids[id] = { id, name, path };
  });

  return ids;
};

const incrementRequestId = (draft) => {
  draft.lists.requestId++;
};

export default {
  gdrive: createImmerReducer(GDriveRecord(), {
    [LOAD_TREE_FOLDERS.PENDING]: ({ tree }, { payload: { id } }) => {
      tree.statuses[id] = STATUSES.PENDING;
    },

    [LOAD_TREE_FOLDERS.FAILURE]: ({ tree }, { payload: { id } }) => {
      tree.statuses[id] = STATUSES.FAILURE;
    },

    [LOAD_TREE_FOLDERS.SUCCESS]: ({ tree }, { payload: { result } }) => {
      addTreeFolders(tree, result, STATUSES.SUCCESS);
    },

    [LOAD_NAVIGATION_FOLDERS.PENDING]: ({ lists }) => {
      lists[ASSETS].folders.status = STATUSES.PENDING;
    },

    [LOAD_NAVIGATION_FOLDERS.FAILURE]: ({ lists }) => {
      lists[ASSETS].folders.status = STATUSES.FAILURE;
    },

    [LOAD_NAVIGATION_FOLDERS.SUCCESS]: (draft, { payload: { result, options } }) => {
      if (draft.lists.requestId === options.requestId) {
        const { items, cursor } = result;
        const { folders } = draft.lists[ASSETS];

        folders.ids = updateFoldersIds(folders.ids, items);
        folders.status = STATUSES.SUCCESS;
        folders.data = folders.data.concat(items);
        folders.cursor = cursor;

        updateTreeFoldersFromNavigation(draft, result, options);
      }
    },

    [LOAD_NAVIGATION_FILES.PENDING]: ({ lists }) => {
      lists[ASSETS].files.status = STATUSES.PENDING;
    },

    [LOAD_NAVIGATION_FILES.FAILURE]: ({ lists }) => {
      lists[ASSETS].files.status = STATUSES.FAILURE;
    },

    [LOAD_NAVIGATION_FILES.SUCCESS]: (draft, { payload: { result, options } }) => {
      if (draft.lists.requestId === options.requestId) {
        const { items, cursor } = result;
        const { files } = draft.lists[ASSETS];

        files.status = STATUSES.SUCCESS;
        files.data = files.data.concat(items);

        files.cursor = cursor;
      }
    },

    [LOAD_SEARCH_FOLDERS.PENDING]: ({ lists }) => {
      lists[SEARCH].folders.status = STATUSES.PENDING;
    },

    [LOAD_SEARCH_FOLDERS.FAILURE]: ({ lists }) => {
      lists[SEARCH].folders.status = STATUSES.FAILURE;
    },

    [LOAD_SEARCH_FOLDERS.SUCCESS]: (draft, { payload: { result, options } }) => {
      if (draft.lists.requestId === options.requestId) {
        const { items, cursor } = result;
        const { folders } = draft.lists[SEARCH];

        folders.ids = updateFoldersIds(folders.ids, items);
        folders.status = STATUSES.SUCCESS;
        folders.data = folders.data.concat(items);
        folders.cursor = cursor;
      }
    },

    [LOAD_SEARCH_FILES.PENDING]: ({ lists }) => {
      lists[SEARCH].files.status = STATUSES.PENDING;
    },

    [LOAD_SEARCH_FILES.FAILURE]: ({ lists }) => {
      lists[SEARCH].files.status = STATUSES.FAILURE;
    },

    [LOAD_SEARCH_FILES.SUCCESS]: (draft, { payload: { result, options } }) => {
      if (draft.lists.requestId === options.requestId) {
        const { items, cursor } = result;
        const { files } = draft.lists[SEARCH];

        files.status = STATUSES.SUCCESS;
        files.data = files.data.concat(items);
        files.cursor = cursor;
      }
    },

    [SET_NAVIGATION_CURRENT_FOLDER]: (draft, { payload: { id, path, kind, driveId } }) => {
      const { lists, currentContext } = draft;
      const currentFolderId = lists.ASSETS.params.id;

      if (currentFolderId !== id) {
        incrementRequestId(draft);

        const previousFolder = lists[currentContext].folders.data.find((folder) => folder.id === id);

        const { ids } = lists[ASSETS].folders;

        lists[ASSETS] = getNewContextList(ASSETS, ids, {
          id,
          previousFolder,
          path,
          kind,
          driveId,
        });

        setSearch(draft, { keepCurrentData: true });
      }
    },

    [SET_SORT]: (draft, { payload }) => {
      incrementRequestId(draft);
      draft.sort = payload;
      const { ids } = draft.lists[ASSETS].folders;
      draft.lists[ASSETS] = getNewContextList(ASSETS, ids, draft.lists[ASSETS].params);
    },

    [SET_ORDER_BY]: (draft, { payload }) => {
      incrementRequestId(draft);
      draft.orderBy = payload;
      const { ids } = draft.lists[ASSETS].folders;
      draft.lists[ASSETS] = getNewContextList(ASSETS, ids, draft.lists[ASSETS].params);
    },

    [SET_SEARCH_TERM]: (draft, { payload: { term } }) => {
      const currentTerm = draft.lists.SEARCH.params.term;

      if (currentTerm !== term) {
        setSearch(draft, { term });
      }
    },

    [CLEAR_SEARCH_TERM]: (draft) => {
      setSearch(draft);
    },

    [SET_BREADCRUMBS_PATH]: (draft) => {
      const { lists, tree, currentContext } = draft;
      const folders = {
        ...searchResults,
        ...lists[ASSETS].folders.ids,
        ...tree.folders,
        ...lists[SEARCH].folders.ids,
      };

      const currentPath = currentContext === SEARCH ? [SEARCH_RESULTS_ID] : lists[ASSETS].params.path;

      draft.breadcrumbPath = currentPath.map((folderId) => {
        const { id, name, path, kind, driveId } = folders[folderId];
        return {
          id,
          name,
          path,
          kind,
          driveId,
        };
      });
    },

    [SET_AUTH_INFO]: (draft, { payload }) => ({
      ...draft,
      ...payload.authInfo,
      isAuthInProgress: false,
      waitForSignIn: false,
    }),

    [SET_AUTH_START]: (draft) => {
      draft.isAuthInProgress = true;
    },

    [SET_USER_START_SIGN_IN]: (draft) => {
      if (draft.userNotLoggedIn) {
        draft.userNotLoggedIn = false;
      }
    },

    [SET_FILE_SELECTION]: (draft, action) => {
      doListsSelection(draft, action);
    },

    [SET_MULTI_FILE_SELECTION]: (draft, action) => {
      doListsMultipleSelection(draft, action);
    },

    [CLEAR_IMAGE_SELECTION]: (draft, { payload }) => {
      draft.lists[payload.context].selected = [];
    },

    [TOGGLE_PANEL]: (draft) => {
      draft.panelIsOpen = !draft.panelIsOpen;
    },

    [SET_FETCHING_FILE_DATA]: (draft, { payload }) => {
      draft.fetchingUploadData = payload.fetching;
    },

    [SET_WAITING_SIGN_IN]: (draft, { payload }) => {
      draft.waitForSignIn = payload.waiting;
      draft.userNotLoggedIn = payload.notLoggedIn || draft.userNotLoggedIn;
    },

    [CLEAR_TAB_STATE]: (draft) => {
      const { lists, currentContext } = draft;

      lists[currentContext].selected = [];
      draft.currentContext = ASSETS;
      draft.breadcrumbPath = [];

      const defaultSortOrder = getDefaultSortOrder();
      draft.orderBy = defaultSortOrder.orderBy;
      draft.sort = defaultSortOrder.sort;

      lists[ASSETS] = getNewContextList(ASSETS, lists[ASSETS].folders.ids);
    },

    [SET_USER_LOGOUT_SUCCESS]: () => {
      const newState = GDriveRecord();
      newState.userNotLoggedIn = true;
      return newState;
    },
  }),
};
